import React, { useEffect } from 'react';
import Modal from 'react-modal';

type DialogProps = {
  children: React.ReactNode;
  isOpen: boolean;
  onClose?: () => void;
};

const Dialog = ({ children, isOpen, onClose }: DialogProps) => {
  /**
   * This useEffect hook is responsible for managing the HTML class and scroll behavior of the dialog component.
   * It adds the "no-scroll" class to the document's root element when the dialog is open, and removes it when the dialog is closed and there are no other dialog elements present.
   * It also observes changes in the document's structure using a MutationObserver, and updates the HTML class accordingly. It's mainly needed to prevent issues from rendering multiple dialogs or nested dialogs.
   * Additionally, it listens for window resize events and updates the HTML class accordingly.
   *
   * @param {boolean} isOpen - Indicates whether the dialog is open or closed.
   */
  useEffect(() => {
    const handleHtmlClass = () => {
      const elements = document.querySelectorAll('.dialog-content');
      const length = elements.length;

      if (isOpen) {
        document.documentElement.classList.add('no-scroll');
      } else if (!isOpen && length === 0) {
        document.documentElement.classList.remove('no-scroll');
      }
    };

    // Create a MutationObserver instance
    const observer = new MutationObserver((mutationsList, observer) => {
      for (let mutation of mutationsList) {
        if (mutation.type === 'childList') {
          handleHtmlClass();
        }
      }
    });

    // Start observing the document with the configured parameters
    observer.observe(document, { childList: true, subtree: true });

    handleHtmlClass();

    window.addEventListener('resize', handleHtmlClass);

    return () => {
      // Stop observing changes
      observer.disconnect();

      window.removeEventListener('resize', handleHtmlClass);
    };
  }, [isOpen]);

  return (
    <Modal
      ariaHideApp={false} // console error suppression
      isOpen={isOpen}
      overlayClassName={'my-account-dialog-overlay'}
      className={'my-account-dialog'}
      onRequestClose={onClose}
    >
      <div className="dialog-overlay"></div>
      <div className="dialog-content">{children}</div>
    </Modal>
  );
};

export default Dialog;
